/* eslint-disable react/no-unknown-property */
import {
  Dropdown,
  Icon,
  Menu,
  MenuItem,
  MenuItemWC,
  NavLink,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  type TranslationKeys,
  useTranslation,
} from '@pypestream/translations';
import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHref, useNavigate } from 'react-router';

import { isPlainLeftClick } from '../../utils/solution-link';
import { NavLinkWrapper } from './nav-link-wrapper';
import { useNavOverflow } from './use-nav-overflow';

export type OverflowNavItem = {
  path: string;
  label: string;
  i18nKey?: string;
};

const MORE_TRIGGER_ID = 'ps-nav-more-trigger';

const OverflowMenuLink: FC<{
  item: OverflowNavItem;
  isActive: boolean;
  onClose: () => void;
}> = ({ item, isActive, onClose }) => {
  const href = useHref(item.path);
  const navigate = useNavigate();
  return (
    // Use ps-menu-item's first-class `href` support (PSTUDIO-3773) so the
    // ps-menu-item host itself stays the direct slotted child of <Menu> —
    // wrapping it in a light-DOM <a> would make the <a> the slotted child and
    // break ps-menu's roving focus (Arrow/Home/End/Enter). The WC renders a
    // real inner <a href> for native middle-click / right-click "Open in new
    // tab"; a plain left-click is intercepted here for SPA navigation, modified
    // / middle clicks fall through to the anchor and just close the dropdown.
    // NOTE: @lit/react delivers the NATIVE DOM event to onClick/onAuxClick (not
    // a React SyntheticEvent), so read top-level fields directly — never
    // `event.nativeEvent` (it is undefined here).
    <MenuItem
      href={href}
      test-id={`nav-more-item-${item.label.toLowerCase()}`}
      selected={isActive}
      onClick={(event: React.MouseEvent<MenuItemWC>) => {
        if (event.defaultPrevented) return;
        if (!isPlainLeftClick(event)) {
          onClose();
          return;
        }
        event.preventDefault();
        onClose();
        navigate(item.path);
      }}
      onAuxClick={(event: React.MouseEvent<MenuItemWC>) => {
        if (event.button === 1) onClose();
      }}
    >
      {item.i18nKey ? (
        <TranslationComponent i18nKey={item.i18nKey as TranslationKeys}>
          {item.label}
        </TranslationComponent>
      ) : (
        item.label
      )}
    </MenuItem>
  );
};

/**
 * Renders the centered nav tabs, collapsing the ones that don't fit — from the
 * end — into a "…" overflow menu (Figma "Universal Nav / Prostudio Solution
 * Manual validation", PSTUDIO-3720). How many fit is decided by the measured
 * `useNavOverflow` hook; the hidden measurement rows keep the natural item /
 * trigger widths available so both shrink and grow-back recompute with no flash.
 */
export const NavLinksOverflow: FC<{
  items: OverflowNavItem[];
  pathname: string;
}> = ({ items, pathname }) => {
  const { t } = useTranslation();
  const depKey = items.map((i) => i.label).join('|');
  const { containerRef, measureRef, triggerMeasureRef, visibleCount } =
    useNavOverflow(items.length, depKey);
  const [open, setOpen] = useState(false);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  const hasOverflow = overflow.length > 0;

  const moreLabel = t(
    'pro-studio/navigation:nav_links.more' as TranslationKeys,
    {
      defaultValue: 'More',
    }
  );

  return (
    <div
      className="c-nav-links-overflow"
      ref={containerRef as React.RefObject<HTMLDivElement>}
    >
      {/* Hidden measurement rows render FIRST (they are `position: absolute`,
          so source order changes neither their measured widths nor the visible
          layout) so the true last VISIBLE item stays the DOM `:last-child`.
          Otherwise a trailing measure div is always `:last-child`, ps-nav-link's
          `:host(:last-child){ margin-inline-end: 0 }` reset never fires, and the
          unpaired trailing margin shifts the centered tab group ~8px left. */}
      {/* Measurement row: ALL items at natural width, out of flow. */}
      <div
        className="c-nav-links-overflow__measure"
        aria-hidden="true"
        ref={measureRef as React.RefObject<HTMLDivElement>}
      >
        {items.map(({ label, i18nKey }) => (
          <NavLink key={label}>
            {i18nKey ? (
              <TranslationComponent i18nKey={i18nKey as TranslationKeys}>
                {label}
              </TranslationComponent>
            ) : (
              label
            )}
          </NavLink>
        ))}
      </div>
      {/* Hidden trigger clone for its reserved width. */}
      <div
        className="c-nav-links-overflow__measure"
        aria-hidden="true"
        ref={triggerMeasureRef as React.RefObject<HTMLDivElement>}
      >
        <NavLink>
          <Icon name="meatballs" size="small" />
        </NavLink>
      </div>

      {visible.map(({ path: to, label, i18nKey }) => (
        <NavLinkWrapper
          label={label}
          key={label}
          to={to}
          isActive={pathname === to}
          i18nKey={i18nKey as TranslationKeys}
        >
          {label}
        </NavLinkWrapper>
      ))}

      {hasOverflow && (
        <>
          <NavLink
            id={MORE_TRIGGER_ID}
            test-id="nav-link-more"
            aria-label={moreLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            title={moreLabel}
            active={overflow.some((i) => i.path === pathname)}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name="meatballs" size="small" />
            {/* Visually-hidden label gives the shadow-DOM <button> a real
                accessible name — an aria-label on the ps-nav-link host does not
                propagate to the inner button's name. */}
            <span
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {moreLabel}
            </span>
          </NavLink>

          {/* Controlled DS Dropdown, portalled to <body>. The `createPortal`
              is UNCONDITIONAL (mirrors CellErrorTooltip, PSTUDIO-2321): a
              conditionally-rendered portal changes the fiber's sibling position
              and reintroduces the "removeChild ... not a child" crash that this
              ticket (PSTUDIO-3830) set out to kill. So it renders whenever
              `hasOverflow` and visibility is driven by `active`. `omitPortal`
              keeps the DS popover in the React-owned subtree (no WC-side portal
              move), while React's own createPortal escapes the nav's
              `overflow: hidden`. `disableTriggerListeners` + `active` makes it
              fully controlled — the trigger toggles `open`; DS `onClose` (Esc /
              click-outside) resets it so `aria-expanded` stays in sync. The DS
              ps-dropdown now draws the surface + z-index:100000, focus-trap,
              Esc, and floating-ui positioning (anchored by `trigger` id). */}
          {createPortal(
            <Dropdown
              trigger={MORE_TRIGGER_ID}
              active={open && hasOverflow}
              placement="bottom-end"
              disableTriggerListeners
              omitPortal
              omit-portal
              keepMounted
              test-id="nav-more-dropdown"
              onclose={() => setOpen(false)}
            >
              <Menu>
                {overflow.map((item) => (
                  <OverflowMenuLink
                    key={item.label}
                    item={item}
                    isActive={pathname === item.path}
                    onClose={() => setOpen(false)}
                  />
                ))}
              </Menu>
            </Dropdown>,
            document.body
          )}
        </>
      )}
    </div>
  );
};
